/**
 * BUSINESS LOGIC (BL): PERSONAL ROULETTE (RULETA BAJA CUP)
 * Capa de lógica de negocio, validaciones bilingües (ES/EN), gestión de sesión local,
 * textos legales CCPA, términos y condiciones oficiales y orquestación de servicios.
 */

import {
    GetPublicConfig as DLGetPublicConfig,
    ParticipateInit as DLParticipateInit,
    TrackMetric as DLTrackMetric,
    SpinRoulette as DLSpinRoulette,
    TrackAction as DLTrackAction,
    GetAdminConfig as DLGetAdminConfig,
    UpdateAdminConfig as DLUpdateAdminConfig,
    GetAdminStats as DLGetAdminStats,
    GetAdminParticipants as DLGetAdminParticipants,
    ToggleActivePersonalRoulette as DLToggleActivePersonalRoulette,
    ResetDataPersonalRoulette as DLResetDataPersonalRoulette,
    GetExportDataset as DLGetExportDataset,
    LookupVoucher as DLLookupVoucher,
    RedeemPrize as DLRedeemPrize,
    RestituteParticipant as DLRestituteParticipant,
    SetQueueIndex as DLSetQueueIndex,
    UpdatePrizeQueue as DLUpdatePrizeQueue,
    ToggleVipBoost as DLToggleVipBoost
} from '../DL/PersonalRoulette';
import * as XLSX from 'xlsx';

// Clave única de almacenamiento local
export const STORAGE_KEY = 'interaktia_personal_roulette_fan_data';

// =========================================================================
// 1. VALIDACIONES DE FORMULARIO
// =========================================================================

/**
 * Valida el nombre completo (mínimo 3 caracteres alfanuméricos, no vacío).
 * @param {string} name 
 * @returns {boolean}
 */
export const validateFullName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    return trimmed.length >= 3;
};

/**
 * Valida formato de correo electrónico según estándar RFC 5322.
 * @param {string} email 
 * @returns {boolean}
 */
export const validateEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    const trimmed = email.trim();
    // Expresión regular robusta compatible con RFC 5322 simplificada
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return regex.test(trimmed);
};

/**
 * Valida número de teléfono a 10 dígitos numéricos (formato binacional US/MX).
 * @param {string} phone 
 * @returns {boolean}
 */
export const validatePhone = (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    const cleanDigits = phone.replace(/\D/g, '');
    return cleanDigits.length === 10;
};

/**
 * Valida código postal ZIP de 5 dígitos numéricos.
 * @param {string} zipCode 
 * @returns {boolean}
 */
export const validateZipCode = (zipCode) => {
    if (!zipCode || typeof zipCode !== 'string') return false;
    const cleanDigits = zipCode.replace(/\D/g, '');
    return cleanDigits.length === 5;
};

/**
 * Valida todo el formulario de registro y retorna mapa de errores bilingüe.
 * @param {object} formData 
 * @param {string} lang 'es' | 'en'
 * @returns {{ isValid: boolean, errors: object }}
 */
/**
 * Valida todo el formulario de registro y retorna mapa de errores bilingüe según la región (MX o USA).
 * @param {object} formData 
 * @param {string} lang 'es' | 'en'
 * @param {string} region 'MX' | 'USA'
 * @returns {{ isValid: boolean, errors: object }}
 */
export const validateRegistrationForm = (formData = {}, lang = 'es', region = 'MX') => {
    const isUsa = (region || '').toUpperCase() === 'USA';
    const t = getDictionary(lang, region);
    const errors = {};

    if (!validateFullName(formData.fullName)) {
        errors.fullName = t.errors.fullName;
    }

    if (!validateEmail(formData.email)) {
        errors.email = t.errors.email;
    }

    if (!validatePhone(formData.phone)) {
        errors.phone = t.errors.phone;
    }

    // Código postal solo es obligatorio si la sala está configurada para región USA
    if (isUsa) {
        if (!validateZipCode(formData.zipCode)) {
            errors.zipCode = t.errors.zipCode;
        }
    }

    if (!formData.acceptedTerms) {
        errors.acceptedTerms = t.errors.acceptedTerms;
    }

    if (!formData.acceptedPrivacyCCPA && !formData.acceptedPrivacy) {
        errors.acceptedPrivacyCCPA = t.errors.acceptedPrivacyCCPA;
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// =========================================================================
// 2. TEXTOS LEGALES Y DICCIONARIOS BILINGÜES (MÉXICO Y USA)
// =========================================================================

// --- TEXTOS LEGALES MÉXICO (LFPDPPP / DERECHOS ARCO) ---
export const DEFAULT_TERMS_TEXT_MX_ES = `TÉRMINOS Y CONDICIONES OFICIALES DE LA DINÁMICA
1. REQUISITO DE PARTICIPACIÓN (18+): Esta dinámica es válida exclusivamente para personas físicas mayores de 18 años cumplidos al momento del registro con residencia en los Estados Unidos Mexicanos.
2. ORGANIZADOR Y DESLINDE DE RESPONSABILIDAD: La dinámica es organizada por el Club Tijuana Xoloitzcuintles de Caliente y sus patrocinadores oficiales. Interaktia y Five Digital Minds actúan estrictamente como proveedores de infraestructura y software tecnológico. El participante libera y deslinda formalmente a Interaktia, Five Digital Minds y a sus filiales de cualquier reclamación, controversia o daño derivado de la dinámica o entrega de premios.
3. LÍMITE DE PARTICIPACIÓN: Se permite un único (1) registro y tiro de ruleta por persona durante la jornada o activación. Cualquier intento de duplicidad por correo electrónico, teléfono celular o alteración técnica invalidará automáticamente la participación.
4. PREMIOS Y VIGENCIA: Los premios obtenidos en la ruleta son personales e intransferibles, no canjeables por dinero en efectivo ni acumulables con otras promociones. El canje debe realizarse exclusivamente en el módulo oficial de atención del evento en la fecha correspondiente mostrando el folio digital.
5. DISPONIBILIDAD DE EXISTENCIAS: Todos los premios físicos están sujetos a disponibilidad de stock en el recinto. Las decisiones del comité organizador son definitivas e inapelables.
6. JURISDICCIÓN: Para cualquier controversia, las partes se someten a las leyes federales aplicables de los Estados Unidos Mexicanos y a los tribunales competentes de Tijuana, Baja California.`;

export const DEFAULT_TERMS_TEXT_MX_EN = `OFFICIAL SWEEPSTAKES TERMS AND CONDITIONS
1. AGE REQUIREMENT (18+): This promotion is open exclusively to individuals who are eighteen (18) years of age or older legally residing in Mexico.
2. ORGANIZER & LIABILITY DISCLAIMER: This activation is hosted by the organizing Club Tijuana Xoloitzcuintles de Caliente and official sponsors. Interaktia and Five Digital Minds act solely as technology and software providers. The participant hereby releases, waives, and holds harmless Interaktia, Five Digital Minds, the venue, and their respective affiliates from any and all claims or liabilities.
3. ENTRY LIMIT: Strictly limited to one (1) registration and spin per person during the event. Any duplicate attempts using identical email, phone number, or automated means will result in immediate disqualification.
4. PRIZES & REDEMPTION: Prizes are non-transferable, non-assignable, and cannot be redeemed for cash. Prizes must be claimed exclusively at the official on-site support booth during the event upon presenting the digital voucher folio.
5. STOCK AVAILABILITY: All physical prizes are subject to real-time on-site availability. All decisions of the organizing committee are final and binding.
6. GOVERNING LAW: This promotion shall be governed by and construed in accordance with the applicable laws of Mexico and the competent courts of Tijuana, Baja California.`;

export const DEFAULT_PRIVACY_TEXT_MX_ES = `AVISO DE PRIVACIDAD INTEGRAL Y CONSENTIMIENTO DE DATOS
En cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y su Reglamento:
1. DATOS RECABADOS: Recopilamos las siguientes categorías de datos personales: Nombre completo, correo electrónico, número de teléfono móvil / WhatsApp, dirección IP y datos de navegación técnica.
2. FINALIDADES DEL TRATAMIENTO: Sus datos personales serán procesados para:
   a) Registrar y autenticar su participación en la dinámica de Ruleta.
   b) Validar su identidad, emitir su folio digital y gestionar el canje de premios en sitio.
   c) Envío de comunicaciones comerciales, boletines, noticias, promociones y ofertas exclusivas de Five Digital Minds, el Club Tijuana Xoloitzcuintles de Caliente, patrocinadores oficiales y aliados comerciales.
   d) Elaboración de análisis demográficos y estadísticas agregadas de la activación.
3. TRANSFERENCIA DE DATOS: Al marcar la casilla de aceptación, usted otorga su consentimiento expreso para que sus datos sean compartidos entre Five Digital Minds, el Club Tijuana Xoloitzcuintles de Caliente y sus patrocinadores comerciales autorizados.
4. DERECHOS ARCO: Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al tratamiento de sus datos personales (Derechos ARCO) escribiendo a: contacto@interaktia.com.`;

export const DEFAULT_PRIVACY_TEXT_MX_EN = `PRIVACY NOTICE & PERSONAL DATA CONSENT
In compliance with the Mexican Federal Law on the Protection of Personal Data Held by Private Parties (LFPDPPP):
1. CATEGORIES OF DATA COLLECTED: We collect the following personal data: Full name, email address, mobile phone number / WhatsApp, IP address, and device telemetry data.
2. PURPOSES OF PROCESSING: Your personal data is processed to:
   a) Register and authenticate your entry into the Roulette dynamic.
   b) Verify identity, generate your unique digital folio, and manage on-site prize redemption.
   c) Deliver marketing communications, newsletters, match updates, and exclusive sponsor promotions.
   d) Generate aggregate demographic, foot-traffic, and marketing analytics.
3. DATA TRANSFER: By checking this box, you provide your explicit consent to share your contact information with Five Digital Minds, the organizing Club Tijuana Xoloitzcuintles de Caliente, and official corporate sponsors.
4. ARCO RIGHTS: You have the right to Access, Rectify, Cancel, or Oppose the processing of your personal data (ARCO Rights) by emailing: contacto@interaktia.com.`;

export const DEFAULT_ARCO_TEXT_ES = `GUÍA PARA EL EJERCICIO DE DERECHOS ARCO (MÉXICO)
Conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), usted tiene derecho en todo momento a Acceder, Rectificar, Cancelar u Oponerse al tratamiento de sus datos personales:

¿CÓMO PRESENTAR SU SOLICITUD ARCO?
1. Envíe un correo electrónico a contacto@interaktia.com con el asunto: "Ejercicio de Derechos ARCO - Ruleta".
2. Adjunte: Nombre completo, correo electrónico y teléfono registrados.
3. Describa de manera clara y precisa los datos personales respecto de los que busca ejercer alguno de los derechos ARCO.
4. Su solicitud será atendida en un plazo máximo de 20 días hábiles conforme a la legislación mexicana.`;

export const DEFAULT_ARCO_TEXT_EN = `GUIDE TO EXERCISING ARCO RIGHTS (MEXICO)
Under Mexico's Federal Law on Protection of Personal Data Held by Private Parties (LFPDPPP), you have the right to Access, Rectify, Cancel, or Oppose the processing of your personal data at any time:

HOW TO SUBMIT YOUR ARCO REQUEST:
1. Send an email to contacto@interaktia.com with subject: "ARCO Rights Request - Roulette".
2. Include: Full name, registered email address, and phone number.
3. Clearly state the personal data and the specific ARCO right you wish to exercise.
4. Your request will be acknowledged and processed within 20 business days as required by Mexican law.`;

// --- TEXTOS LEGALES USA (CALIFORNIA CCPA / CPRA) ---
export const DEFAULT_TERMS_TEXT_ES = `TÉRMINOS Y CONDICIONES OFICIALES DE LA DINÁMICA
1. REQUISITO DE MAYORÍA DE EDAD (18+): Esta dinámica es válida exclusivamente para personas físicas mayores de 18 años cumplidos al momento del registro. Al participar, el aficionado certifica bajo protesta de decir verdad que cumple con la mayoría de edad legal.
2. ORGANIZADOR Y DESLINDE DE RESPONSABILIDAD: La dinámica es organizada por el Club Tijuana Xoloitzcuintles de Caliente. Interaktia y Five Digital Minds actúan estrictamente como proveedores de infraestructura y software tecnológico. El participante libera y deslinda formalmente a Interaktia, Five Digital Minds, al recinto sede y a sus filiales de cualquier reclamo, controversia o daño directo o indirecto derivado de la dinámica o entrega de premios.
3. LÍMITE DE PARTICIPACIÓN: Se permite un único (1) registro y tiro de ruleta por persona durante la jornada o activación. Cualquier intento de duplicidad por correo electrónico, teléfono celular o alteración técnica invalidará automáticamente la participación.
4. PREMIOS Y VIGENCIA: Los premios obtenidos en la ruleta son personales e intransferibles, no canjeables por dinero en efectivo ni acumulables con otras promociones. El canje debe realizarse exclusivamente en el módulo oficial de atención del evento en la fecha correspondiente mostrando el folio digital.
5. DISPONIBILIDAD DE EXISTENCIAS: Todos los premios físicos están sujetos a disponibilidad de stock en el sitio. Si el stock se agota, el sistema asignará la casilla de cortesía. Las decisiones del comité organizador son definitivas e inapelables.
6. JURISDICCIÓN: Para cualquier controversia, las partes se someten a las leyes aplicables del Estado de California, Estados Unidos.`;

export const DEFAULT_TERMS_TEXT_EN = `OFFICIAL SWEEPSTAKES TERMS AND CONDITIONS
1. AGE REQUIREMENT (18+): This promotion is open exclusively to legal residents who are eighteen (18) years of age or older at the time of registration. By entering, the participant certifies and warrants that they meet the minimum legal age requirement.
2. ORGANIZER & LIABILITY DISCLAIMER: This activation is hosted by the organizing Club Tijuana Xoloitzcuintles de Caliente. Interaktia and Five Digital Minds act solely as technology and software providers. The participant hereby releases, waives, and holds harmless Interaktia, Five Digital Minds, the venue, and their respective affiliates from any and all claims, liabilities, or damages arising out of participation or prize fulfillment.
3. ENTRY LIMIT: Strictly limited to one (1) registration and spin per person during the event. Any duplicate attempts using identical email, phone number, or automated means will result in immediate disqualification.
4. PRIZES & REDEMPTION: Prizes are non-transferable, non-assignable, and cannot be redeemed for cash. Prizes must be claimed exclusively at the official on-site support booth during the event day upon presenting the digital voucher folio.
5. STOCK AVAILABILITY: All physical prizes are subject to real-time on-site availability. If prize inventory is exhausted, courtesy acknowledgement slices will apply. All decisions of the organizing committee are final and binding.
6. GOVERNING LAW: This promotion shall be governed by and construed in accordance with the laws of the State of California, United States.`;

export const DEFAULT_PRIVACY_TEXT_ES = `AVISO DE PRIVACIDAD Y AUTORIZACIÓN CCPA / CPRA
En cumplimiento con la Ley de Privacidad del Consumidor de California (California Consumer Privacy Act - CCPA / CPRA) y regulaciones vigentes:
1. DATOS RECABADOS: Recopilamos las siguientes categorías de información personal (PII): Nombre completo, correo electrónico, número de teléfono móvil / WhatsApp, código postal (ZIP Code), dirección IP y datos de navegación técnica del dispositivo.
2. FINALIDADES DEL TRATAMIENTO: Sus datos personales serán procesados para:
   a) Registrar y autenticar su participación en la dinámica Ruleta Baja Cup.
   b) Validar su identidad, emitir su folio digital y gestionar el canje de premios en sitio.
   c) Envío de comunicaciones comerciales, boletines, noticias, promociones y ofertas exclusivas del Club Tijuana Xoloitzcuintles de Caliente, Interaktia, Five Digital Minds, patrocinadores oficiales y aliados comerciales.
   d) Elaboración de análisis demográficos y estadísticas agregadas de la activación.
3. TRANSFERENCIA Y CESIÓN DE DATOS: Al marcar la casilla de aceptación, usted otorga su consentimiento expreso e informado para que sus datos de contacto sean transferidos y compartidos entre Club Tijuana Xoloitzcuintles de Caliente, Interaktia, Five Digital Minds y sus patrocinadores comerciales autorizados para los fines publicitarios descritos.
4. DERECHOS ARCO Y OPT-OUT: Usted tiene derecho a acceder, rectificar, suprimir sus datos o revocar su consentimiento en cualquier momento mediante el enlace "Do Not Sell or Share My Personal Information" o escribiendo a: contacto@interaktia.com.`;

export const DEFAULT_PRIVACY_TEXT_EN = `PRIVACY NOTICE & CCPA / CPRA DATA AUTHORIZATION
In compliance with the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA):
1. CATEGORIES OF DATA COLLECTED: We collect the following categories of Personally Identifiable Information (PII): Full name, email address, mobile phone number / WhatsApp, ZIP code, IP address, and device telemetry data.
2. PURPOSES OF PROCESSING: Your personal information is processed to:
   a) Register and authenticate your entry into the Baja Cup Roulette dynamic.
   b) Verify identity, generate your unique digital folio, and manage on-site prize redemption.
   c) Deliver marketing communications, newsletters, match updates, and exclusive sponsor promotions from Club Tijuana Xoloitzcuintles de Caliente, Interaktia, Five Digital Minds, and affiliated corporate partners.
   d) Generate aggregate demographic, foot-traffic, and marketing analytics.
3. DATA SHARING & SALE DISCLOSURE: By checking this box, you provide your explicit opt-in consent to share your contact information with Club Tijuana Xoloitzcuintles de Caliente, Interaktia, Five Digital Minds, and official corporate sponsors for commercial and promotional purposes.
4. YOUR PRIVACY RIGHTS & OPT-OUT: Under California law, you have the right to request access to, deletion of, or correction of your personal information, as well as the right to opt-out of data sale or sharing at any time via the "Do Not Sell or Share My Personal Information" link or by emailing: contacto@interaktia.com.`;

export const DEFAULT_DO_NOT_SELL_TEXT_ES = `NO VENDER NI COMPARTIR MI INFORMACIÓN PERSONAL (DERECHOS CCPA / ARCO)
Bajo la Sección 1798.120 del Código Civil de California (CCPA / CPRA), usted tiene el derecho legal inalienable de optar por no autorizar ("Opt-Out") la venta o compartición de su información personal a terceros.

¿CÓMO EJERCER SU DERECHO DE BAJA / OPT-OUT?
Si desea que sus datos personales no sean compartidos con patrocinadores comerciales ni utilizados para prospección comercial:
1. Envíe un correo electrónico a privacy@fivedigitalminds.com indicando en el asunto: "CCPA Opt-Out - Baja Voluntaria Ruleta Baja Cup".
2. Incluya su nombre completo, correo electrónico y número de teléfono utilizados en el registro.
3. Su solicitud será procesada en un plazo máximo de 15 días hábiles conforme a la legislación de California.

Tenga en cuenta que ejercer este derecho no anula su participación previa ni invalida los premios legítimamente ganados en el evento.`;

export const DEFAULT_DO_NOT_SELL_TEXT_EN = `DO NOT SELL OR SHARE MY PERSONAL INFORMATION (CCPA / CPRA OPT-OUT NOTICE)
Under California Civil Code Section 1798.120 (CCPA / CPRA), California residents have the right to opt-out of the "sale" or "sharing" of their personal information for cross-context behavioral advertising and commercial marketing.

HOW TO EXERCISE YOUR OPT-OUT RIGHT:
If you prefer that your personal information not be shared with corporate sponsors or used for promotional marketing:
1. Send an email to contacto@interaktia.com with the subject line: "CCPA Opt-Out Request - Baja Cup Roulette".
2. Include your full name, email address, and phone number used during registration.
3. We will process and acknowledge your request within 15 business days as required by California law.

Please note that opting out does not affect any prizes legitimately won during the event.`;

export const I18N_DICTIONARY = {
    es: {
        langCode: 'es',
        langLabel: 'ES',
        otherLangCode: 'en',
        otherLangLabel: 'EN',
        header: {
            defaultTitle: 'Gira y Gana con Xolos',
            defaultSubtitle: '¡Registra tus datos y gira para ganar premios exclusivos!'
        },
        form: {
            fullNameLabel: 'Nombre Completo',
            fullNamePlaceholder: '',
            emailLabel: 'Correo Electrónico',
            emailPlaceholder: '',
            phoneLabel: 'Teléfono Celular / WhatsApp',
            phonePlaceholder: '',
            zipCodeLabel: 'Código Postal (ZIP Code)',
            zipCodePlaceholder: '5 dígitos (ej. 92101)',
            checkboxTerms: 'He leído y acepto los Términos y Condiciones Oficiales (confirmo ser mayor de 18 años y acepto el deslinde de responsabilidad).',
            checkboxPrivacy: 'Acepto el Aviso de Privacidad y autorizo el tratamiento y cesión de mis datos comerciales de conformidad con la ley CCPA.',
            termsLinkText: 'Términos y Condiciones',
            privacyLinkText: 'Aviso de Privacidad',
            doNotSellLink: 'Do Not Sell or Share My Personal Information (CCPA)',
            submitButton: 'Entrar a la Ruleta',
            submittingButton: 'Validando Registro...'
        },
        errors: {
            fullName: 'Ingresa tu nombre completo.',
            email: 'Ingresa un correo electrónico con formato válido (ej. usuario@dominio.com).',
            phone: 'El teléfono debe contener exactamente 10 dígitos numéricos.',
            zipCode: 'El código postal debe contener exactamente 5 dígitos numéricos.',
            acceptedTerms: 'Debes aceptar los Términos y Condiciones y confirmar mayoría de edad (18+).',
            acceptedPrivacyCCPA: 'Debes aceptar el Aviso de Privacidad CCPA para continuar.',
            genericNetwork: 'Ocurrió un error de conexión con el servidor. Intenta de nuevo.'
        },
        courtesy: {
            badge: 'Dinámica en Pausa',
            title: 'Activación Temporalmente Pausada',
            messageDefault: 'La dinámica de la ruleta se encuentra en pausa o ha finalizado por hoy. ¡Gracias por tu interés!',
            redirectButtonDefault: 'Visitar Sitio Oficial'
        },
        alreadyParticipated: {
            badge: 'Pase Registrado',
            title: '¡Ya Has Participado!',
            subtitle: 'Detectamos que tu correo o teléfono ya cuenta con un tiro completado en esta jornada.',
            folioLabel: 'Folio de Participación',
            prizeLabel: 'Resultado Asignado',
            playedAtLabel: 'Fecha y Hora',
            statusLabel: 'Estatus del Pase',
            statusCompleted: 'Completado',
            statusRedeemed: 'Canjeado',
            statusPending: 'Pendiente de Canje',
            instructionsTitle: 'Instrucciones de Canje',
            instructionsDefault: 'Muestra este folio o cupón digital en el Módulo de Atención de la Entrada Principal para reclamar tu premio físico.',
            sharedDeviceNotice: '¿Otra persona quiere participar? Haz clic en este botón',
            newParticipantButton: 'Participar con otra persona'
        },
        readyToSpin: {
            badge: 'Pase Activo',
            title: '¡Registro Exitoso!',
            subtitle: 'Tu One-Time Token (OTT) ha sido verificado. ¡Todo listo para girar!',
            welcomePrefix: 'Bienvenido/a, ',
            ottNotice: 'Pase criptográfico de un solo uso activo. Duración de sesión: 5 minutos.',
            spinButton: '¡GIRAR RULETA!',
            spinningButton: 'Girando Ruleta...',
            spinInstruction: 'Toca el botón dorado para poner a girar la ruleta.',
            statusWaitingEngine: 'Conectando con el motor SVG GPU...',
            soundOn: 'Sonido Activado',
            soundMuted: 'Sonido Silenciado'
        },
        result: {
            winnerBadge: '¡Premio Ganado!',
            courtesyBadge: 'Sigue participando',
            winnerTitle: '¡FELICIDADES!',
            courtesyTitle: '¡GRACIAS POR PARTICIPAR!',
            winnerSubtitle: '¡Has ganado este increíble premio en Gira y Gana con Xolos!',
            courtesySubtitle: 'Gracias por apoyar a tu equipo con toda la pasión.',
            folioLabel: 'Folio Digital de Canje',
            instructionsTitle: 'Instrucciones para Canjear',
            instructionsDefault: 'Muestra este folio oficial en el Módulo de Atención de la Entrada Principal durante el evento.',
            redeemPending: 'Pendiente de Canje en Sitio',
            redeemDone: 'Premio Canjeado con Éxito',
            anotherPlayerBtn: 'Participar con otra persona',
            saveToGalleryBtn: 'Guardar en Galería',
            savingVoucher: 'Generando Cupón...',
            voucherSavedSuccess: '¡Cupón guardado con éxito en tu galería!',
            qrInstructions: 'Presenta este código QR en el Módulo Oficial de Canje.',
            qrBadge: 'Código QR Oficial',
            thanksTitle: '¡GRACIAS POR PARTICIPAR!',
            thanksSubtitle: 'Agradecemos tu pasión y entrega apoyando al equipo.',
            thanksMessage: 'Sigue disfrutando de la experiencia y las activaciones del evento.',
            visitOfficialSite: 'Visitar Sitio Oficial'
        },
        modals: {
            termsTitle: 'Términos y Condiciones Oficiales',
            privacyTitle: 'Aviso de Privacidad y Consentimiento CCPA',
            doNotSellTitle: 'Do Not Sell or Share My Personal Information',
            closeButton: 'Entendido y Cerrar',
            ageConfirmationBadge: 'Mayoría de edad 18+ obligatoria'
        },
        toast: {
            resetSuccess: 'Formulario listo para un nuevo participante.',
            languageChanged: 'Idioma cambiado a Español.',
            soundEnabled: 'Sonido activado.',
            soundMuted: 'Sonido silenciado.',
            voucherDownloaded: '¡Cupón descargado! Guárdalo en tu galería.',
            screenshotDetected: 'Captura de pantalla detectada.'
        }
    },
    en: {
        langCode: 'en',
        langLabel: 'EN',
        otherLangCode: 'es',
        otherLangLabel: 'ES',
        header: {
            defaultTitle: 'Spin and Win with Xolos',
            defaultSubtitle: 'Register your info and spin to win exclusive prizes!'
        },
        form: {
            fullNameLabel: 'Full Name',
            fullNamePlaceholder: '',
            emailLabel: 'Email Address',
            emailPlaceholder: '',
            phoneLabel: 'Mobile Phone / WhatsApp',
            phonePlaceholder: '',
            zipCodeLabel: 'ZIP Code',
            zipCodePlaceholder: '5 digits (e.g. 92101)',
            checkboxTerms: 'I have read and agree to the Official Terms and Conditions (I confirm I am 18+ years old and accept the liability disclaimer).',
            checkboxPrivacy: 'I accept the Privacy Notice and authorize the processing and commercial sharing of my data under the CCPA.',
            termsLinkText: 'Terms and Conditions',
            privacyLinkText: 'Privacy Notice',
            doNotSellLink: 'Do Not Sell or Share My Personal Information (CCPA)',
            submitButton: 'Enter Roulette',
            submittingButton: 'Validating Registration...'
        },
        errors: {
            fullName: 'Please enter a valid full name.',
            email: 'Please enter a valid email address (e.g. user@domain.com).',
            phone: 'Phone number must be exactly 10 numeric digits.',
            zipCode: 'ZIP code must be exactly 5 numeric digits.',
            acceptedTerms: 'You must accept the Terms & Conditions and confirm you are 18+ years old.',
            acceptedPrivacyCCPA: 'You must accept the CCPA Privacy Notice to proceed.',
            genericNetwork: 'A network error occurred while connecting to the server. Please try again.'
        },
        courtesy: {
            badge: 'Paused',
            title: 'Dynamic Temporarily Paused',
            messageDefault: 'The roulette dynamic is currently paused or has ended for today. Thank you for your interest!',
            redirectButtonDefault: 'Visit Official Website'
        },
        alreadyParticipated: {
            badge: 'Registered Pass',
            title: 'You Have Already Participated!',
            subtitle: 'Our system indicates your email or phone has already completed a spin in this event.',
            folioLabel: 'Participation Folio',
            prizeLabel: 'Assigned Result',
            playedAtLabel: 'Date & Time',
            statusLabel: 'Pass Status',
            statusCompleted: 'Completed',
            statusRedeemed: 'Redeemed',
            statusPending: 'Pending Redemption',
            instructionsTitle: 'Redemption Instructions',
            instructionsDefault: 'Present this folio or digital voucher at the Main Entrance Support Booth to claim your physical prize.',
            sharedDeviceNotice: 'Does another person want to participate? Click this button',
            newParticipantButton: 'Play with another person'
        },
        readyToSpin: {
            badge: 'Active Pass',
            title: 'Registration Successful!',
            subtitle: 'Your One-Time Token (OTT) is verified. All set to spin!',
            welcomePrefix: 'Welcome, ',
            ottNotice: 'Single-use cryptographic pass active. Session duration: 5 minutes.',
            spinButton: 'SPIN WHEEL!',
            spinningButton: 'Spinning Wheel...',
            spinInstruction: 'Tap the golden button to spin the wheel.',
            statusWaitingEngine: 'Connecting to SVG GPU engine...',
            soundOn: 'Sound ON',
            soundMuted: 'Sound MUTED'
        },
        result: {
            winnerBadge: 'Prize Won!',
            courtesyBadge: 'Commemorative Pass',
            winnerTitle: 'CONGRATULATIONS!',
            courtesyTitle: 'THANK YOU FOR PLAYING!',
            winnerSubtitle: 'You won this exciting prize in the Spin and Win with Xolos!',
            courtesySubtitle: 'Thank you for supporting your team with all your passion.',
            folioLabel: 'Digital Redemption Folio',
            instructionsTitle: 'Redemption Instructions',
            instructionsDefault: 'Present this official voucher folio at the Main Entrance Support Booth during the event.',
            redeemPending: 'Pending On-Site Redemption',
            redeemDone: 'Prize Successfully Redeemed',
            anotherPlayerBtn: 'Play with another person',
            saveToGalleryBtn: 'Save to Gallery',
            savingVoucher: 'Generating Voucher...',
            voucherSavedSuccess: 'Voucher successfully saved to your gallery!',
            qrInstructions: 'Present this QR code at the Official Redemption Booth.',
            qrBadge: 'Official QR Code',
            thanksTitle: 'THANK YOU FOR PARTICIPATING!',
            thanksSubtitle: 'We appreciate your passion and support for the team.',
            thanksMessage: 'Keep enjoying the event experience and activations.',
            visitOfficialSite: 'Visit Official Website'
        },
        modals: {
            termsTitle: 'Official Terms & Conditions',
            privacyTitle: 'Privacy Notice & CCPA Consent',
            doNotSellTitle: 'Do Not Sell or Share My Personal Information',
            closeButton: 'I Understand & Close',
            ageConfirmationBadge: '18+ age requirement mandatory'
        },
        toast: {
            resetSuccess: 'Form ready for a new participant.',
            languageChanged: 'Language switched to English.',
            soundEnabled: 'Sound enabled.',
            soundMuted: 'Sound muted.',
            voucherDownloaded: 'Voucher downloaded! Saved to your gallery.',
            screenshotDetected: 'Screenshot detected.'
        }
    }
};

/**
 * Obtiene el diccionario para un idioma ('es' o 'en') y región ('MX' o 'USA').
 * @param {string} lang 
 * @param {string} region
 * @returns {object}
 */
export const getDictionary = (lang = 'es', region = 'MX') => {
    const normalized = (lang || 'es').toLowerCase().startsWith('en') ? 'en' : 'es';
    const isUsa = (region || '').toUpperCase() === 'USA';
    const dict = JSON.parse(JSON.stringify(I18N_DICTIONARY[normalized] || I18N_DICTIONARY.es));

    if (!isUsa) {
        if (normalized === 'es') {
            dict.form.checkboxPrivacy = 'Acepto el Aviso de Privacidad y autorizo el tratamiento y cesión de mis datos conforme a la LFPDPPP.';
            dict.form.zipCodeLabel = 'Código Postal (C.P.)';
            dict.form.doNotSellLink = 'Derechos ARCO (Acceso, Rectificación, Cancelación y Oposición)';
            dict.modals.privacyTitle = 'Aviso de Privacidad Integral';
            dict.modals.doNotSellTitle = 'Ejercicio de Derechos ARCO';
            dict.errors.acceptedPrivacyCCPA = 'Debes aceptar el Aviso de Privacidad para continuar.';
        } else {
            dict.form.checkboxPrivacy = 'I accept the Privacy Notice and authorize the processing and sharing of my data in accordance with the LFPDPPP.';
            dict.form.zipCodeLabel = 'Postal Code (Optional)';
            dict.form.doNotSellLink = 'ARCO Privacy Rights';
            dict.modals.privacyTitle = 'Comprehensive Privacy Notice';
            dict.modals.doNotSellTitle = 'Exercise of ARCO Rights';
            dict.errors.acceptedPrivacyCCPA = 'You must accept the Privacy Notice to proceed.';
        }
    }
    return dict;
};

/**
 * Detecta el idioma inicial basado en la configuración de la sala o el navegador.
 * @param {string|null} roomDefaultLang 
 * @returns {string} 'es' | 'en'
 */
export const detectInitialLanguage = (roomDefaultLang = null) => {
    if (roomDefaultLang && typeof roomDefaultLang === 'string') {
        const clean = roomDefaultLang.toLowerCase().trim();
        if (clean === 'en' || clean === 'es') return clean;
    }
    const navLang = (navigator.language || navigator.userLanguage || 'es').toLowerCase();
    return navLang.startsWith('en') ? 'en' : 'es';
};

// =========================================================================
// 3. GESTIÓN DE SESIÓN LOCAL (LOCALSTORAGE) Y TELEMETRÍA
// =========================================================================

/**
 * Guarda la sesión del participante en localStorage.
 * @param {object} sessionData 
 */
export const saveParticipantSession = (sessionData) => {
    try {
        if (!sessionData) return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
    } catch (e) {
        console.warn('Error al guardar sesión de Ruleta en localStorage:', e);
    }
};

/**
 * Obtiene la sesión actual del participante de localStorage.
 * @returns {object|null}
 */
export const getParticipantSession = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (e) {
        console.warn('Error al leer sesión de Ruleta en localStorage:', e);
        return null;
    }
};

/**
 * Limpia la sesión del participante para permitir "Participar con otra persona".
 */
export const clearParticipantSession = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        // Generar un nuevo identificador de sesión para aislar el embudo del nuevo aficionado
        const newSessId = 'sess_roulette_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
        sessionStorage.setItem('personal_roulette_session_id', newSessId);
        sessionStorage.removeItem('personal_roulette_form_start_tracked');
        return true;
    } catch (e) {
        console.warn('Error al limpiar sesión de Ruleta:', e);
        return false;
    }
};

/**
 * Obtiene o inicializa el SessionId único para telemetría.
 * @returns {string}
 */
export const getOrCreateSessionId = () => {
    try {
        let sessId = sessionStorage.getItem('personal_roulette_session_id');
        if (!sessId) {
            sessId = 'sess_roulette_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('personal_roulette_session_id', sessId);
        }
        return sessId;
    } catch {
        return 'sess_roulette_fallback_' + Date.now();
    }
};

/**
 * Formatea una fecha ISO o timestamp a cadena legible.
 * @param {string|Date} dateString 
 * @param {string} lang 'es' | 'en'
 * @returns {string}
 */
export const formatEventDate = (dateString, lang = 'es') => {
    if (!dateString) return '--';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return String(dateString);
        return date.toLocaleString(lang === 'en' ? 'en-US' : 'es-MX', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return String(dateString);
    }
};

// =========================================================================
// 4. WRAPPERS DE NEGOCIO (BL)
// =========================================================================

/**
 * Obtiene la configuración pública de la sala.
 * @param {string|null} roomId 
 */
export const GetPublicConfigBL = async (roomId = null) => {
    return await DLGetPublicConfig(roomId);
};

/**
 * Registra a un nuevo aficionado ejecutando validaciones previas y llamando a la API.
 * @param {object} payload 
 * @param {string} lang 'es' | 'en'
 * @param {string} region 'MX' | 'USA'
 */
export const ParticipateInitBL = async (payload = {}, lang = 'es', region = 'MX') => {
    const { isValid, errors } = validateRegistrationForm(payload, lang, region);
    if (!isValid) {
        const firstError = Object.values(errors)[0] || 'Error de validación en los campos del formulario.';
        return {
            success: false,
            message: firstError,
            validationErrors: errors
        };
    }

    // Sanitización y armado de payload limpio
    const cleanPayload = {
        roomId: payload.roomId || null,
        fullName: payload.fullName.trim(),
        email: payload.email.trim().toLowerCase(),
        phone: payload.phone.replace(/\D/g, '').trim(),
        zipCode: payload.zipCode ? payload.zipCode.replace(/\D/g, '').trim() : '',
        languageCode: lang,
        trafficSource: payload.trafficSource || 'direct',
        acceptedTerms: Boolean(payload.acceptedTerms),
        acceptedPrivacyCCPA: Boolean(payload.acceptedPrivacyCCPA || payload.acceptedPrivacy),
        sessionId: payload.sessionId || getOrCreateSessionId(),
        deviceInfo: `${navigator.platform || 'Unknown'} - ${navigator.userAgent ? navigator.userAgent.slice(0, 100) : ''}`,
        userAgent: navigator.userAgent || ''
    };

    return await DLParticipateInit(cleanPayload);
};

/**
 * Registra un hito de telemetría en backend y Google Analytics / GTM.
 * @param {string|null} roomId 
 * @param {string} eventName 
 * @param {string} sessionId 
 * @param {string} source 
 */
export const TrackMetricBL = async (roomId, eventName, sessionId = null, source = null) => {
    if (!eventName || typeof eventName !== 'string') {
        return { success: false, message: 'El nombre del evento es requerido.' };
    }

    const finalSessionId = sessionId || getOrCreateSessionId();
    const payload = {
        roomId,
        eventName: eventName.trim(),
        sessionId: finalSessionId,
        source: source ? source.trim() : 'direct'
    };

    // Disparo local a Backend API
    const res = await DLTrackMetric(payload, roomId);

    // Disparo a GA4 / GTM si están presentes en la ventana global
    try {
        if (window.gtag) {
            window.gtag('event', `personal_roulette_${eventName}`, {
                session_id: finalSessionId,
                event_category: 'personal_roulette',
                event_label: eventName,
                traffic_source: source || 'direct'
            });
        }
        if (window.dataLayer && Array.isArray(window.dataLayer)) {
            window.dataLayer.push({
                event: `personal_roulette_${eventName}`,
                session_id: finalSessionId,
                traffic_source: source || 'direct'
            });
        }
    } catch (e) {
        console.warn('Error en telemetría externa GA4/GTM:', e);
    }

    return res;
};

// =========================================================================
// 5. MOTOR MATEMÁTICO SVG VECTORIAL DEPORTIVO (2 a 12 SECTORES)
// =========================================================================

/**
 * Calcula el ángulo en grados de cada sector/rebanada (360 / N).
 * @param {number} totalSlices (entre 2 y 12)
 * @returns {number}
 */
export const calculateSliceAngle = (totalSlices = 6) => {
    const n = Math.max(2, Math.min(12, Number(totalSlices) || 6));
    return 360 / n;
};

/**
 * Convierte coordenadas polares (radio, ángulo en grados donde 0° es arriba a las 12 en punto)
 * a coordenadas cartesianas (x, y) en el plano SVG.
 * @param {number} cx Centro X
 * @param {number} cy Centro Y
 * @param {number} r Radio
 * @param {number} angleInDegrees Ángulo en grados (0° = 12:00, 90° = 3:00, etc.)
 * @returns {{ x: number, y: number }}
 */
export const polarToCartesian = (cx, cy, r, angleInDegrees) => {
    // 0 grados apunta a las 12 en punto (-90 grados estándar en trigonometría)
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
        x: Number((cx + (r * Math.cos(angleInRadians))).toFixed(3)),
        y: Number((cy + (r * Math.sin(angleInRadians))).toFixed(3))
    };
};

/**
 * Genera el comando path SVG exacto (arco de rebanada) para un sector.
 * @param {number} cx Centro X
 * @param {number} cy Centro Y
 * @param {number} r Radio del sector
 * @param {number} startAngle Ángulo inicial en grados
 * @param {number} endAngle Ángulo final en grados
 * @returns {string} Comando path d SVG
 */
export const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, startAngle);
    const end = polarToCartesian(cx, cy, r, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
};

/**
 * Calcula la posición radial y rotación para el texto del premio en la rebanada.
 * @param {number} cx Centro X
 * @param {number} cy Centro Y
 * @param {number} r Radio total
 * @param {number} startAngle Ángulo de inicio
 * @param {number} endAngle Ángulo de fin
 * @param {number} distanceRatio Distancia relativa desde el centro (default 0.70)
 * @returns {{ x: number, y: number, midAngle: number, rotation: number }}
 */
export const calculateSliceTextPosition = (cx, cy, r, startAngle, endAngle, distanceRatio = 0.70) => {
    const midAngle = (startAngle + endAngle) / 2;
    const pos = polarToCartesian(cx, cy, r * distanceRatio, midAngle);
    return {
        x: pos.x,
        y: pos.y,
        midAngle,
        rotation: midAngle
    };
};

/**
 * Calcula la posición del icono/imagen dentro de la rebanada.
 * @param {number} cx Centro X
 * @param {number} cy Centro Y
 * @param {number} r Radio total
 * @param {number} startAngle Ángulo de inicio
 * @param {number} endAngle Ángulo de fin
 * @param {number} distanceRatio Distancia relativa desde el centro (default 0.44)
 * @returns {{ x: number, y: number, midAngle: number }}
 */
export const calculateSliceIconPosition = (cx, cy, r, startAngle, endAngle, distanceRatio = 0.44) => {
    const midAngle = (startAngle + endAngle) / 2;
    const pos = polarToCartesian(cx, cy, r * distanceRatio, midAngle);
    return {
        x: pos.x,
        y: pos.y,
        midAngle
    };
};

/**
 * Calcula las posiciones polares de los remaches/pegs perimetrales en el anillo exterior metálico.
 * @param {number} cx Centro X
 * @param {number} cy Centro Y
 * @param {number} rimRadius Radio del anillo de remaches
 * @param {number} totalSlices Número de divisiones angulares
 * @returns {Array<{ x: number, y: number, angle: number, index: number }>}
 */
export const calculatePegsPositions = (cx, cy, rimRadius, totalSlices = 6) => {
    const n = Math.max(2, Math.min(12, Number(totalSlices) || 6));
    const sliceAngle = 360 / n;
    const pegs = [];

    for (let i = 0; i < n; i++) {
        const angle = i * sliceAngle;
        const pos = polarToCartesian(cx, cy, rimRadius, angle);
        pegs.push({
            x: pos.x,
            y: pos.y,
            angle,
            index: i
        });
    }

    return pegs;
};

/**
 * Calcula el ángulo final de rotación para detener la ruleta con precisión en el sector objetivo.
 * El puntero superior fijo está a las 12 en punto (0°).
 * @param {number} targetSliceIndex Índice objetivo (0-indexed)
 * @param {number} totalSlices Total de rebanadas (2 a 12)
 * @param {number} currentRotation Rotación previa acumulada en grados
 * @param {number} minFullSpins Giros completos mínimos (default: 6)
 * @param {number} randomOffsetRatio Variación aleatoria (-0.35 a 0.35 del ancho de la rebanada)
 * @returns {number} targetAngle en grados para el CSS transform rotate(Xdeg)
 */
export const calculateTargetRotation = (
    targetSliceIndex,
    totalSlices = 6,
    currentRotation = 0,
    minFullSpins = 6,
    randomOffsetRatio = 0
) => {
    const n = Math.max(2, Math.min(12, Number(totalSlices) || 6));
    const sliceAngle = 360 / n;
    const targetIdx = Math.max(0, Math.min(n - 1, Number(targetSliceIndex) || 0));

    // El centro del sector `targetIdx` se ubica en (targetIdx + 0.5) * sliceAngle
    const sliceCenterAngle = (targetIdx + 0.5) * sliceAngle;

    // Para que el sector termine alineado a las 12 en punto (0°), el giro modular necesario es (360 - sliceCenterAngle)
    const baseOffset = (360 - sliceCenterAngle + 360) % 360;

    // Jitter seguro dentro del 30% del sector para no tocar el remache divisor
    const maxJitter = sliceAngle * 0.30;
    const clampedRatio = Math.max(-1, Math.min(1, randomOffsetRatio));
    const jitter = clampedRatio * maxJitter;

    // Asegurar que la ruleta siempre avance hacia adelante sumando vueltas completas
    const currentBase = currentRotation - (currentRotation % 360);
    const fullSpinsAngle = Math.max(4, minFullSpins) * 360;

    let targetAngle = currentBase + fullSpinsAngle + baseOffset + jitter;
    if (targetAngle <= currentRotation + (minFullSpins - 1) * 360) {
        targetAngle += 360;
    }

    return Number(targetAngle.toFixed(2));
};

/**
 * Generador de UUID v4 estándar seguro (compatible con entornos sin crypto.randomUUID).
 */
export const generateGuid = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * Lista de sectores y premios por defecto con diseño deportivo de alta visibilidad.
 */
export const getDefaultPrizes = () => {
    return [
        {
            id: '8662bf2b-4fcb-488d-aa7f-e362612a2b58',
            textEs: 'GORRA OFICIAL',
            textEn: 'OFFICIAL CAP',
            descriptionEs: 'Gorra conmemorativa Baja Cup 2026',
            descriptionEn: 'Commemorative Baja Cup 2026 Cap',
            backgroundColor: '#dc2626',
            textColor: '#ffffff',
            isWinner: true,
            probabilityWeight: 20
        },
        {
            id: 'ac71371a-b27a-4522-bbf3-807f33bf32b7',
            textEs: 'SIGUE JUGANDO',
            textEn: 'BETTER LUCK',
            descriptionEs: 'Pase de cortesía y agradecimiento',
            descriptionEn: 'Courtesy acknowledgement pass',
            backgroundColor: '#1e293b',
            textColor: '#ffffff',
            isWinner: false,
            probabilityWeight: 50
        },
        {
            id: 'f857a206-c560-48fc-b94f-91fa2c691868',
            textEs: 'JERSEY OFICIAL',
            textEn: 'TEAM JERSEY',
            descriptionEs: 'Jersey oficial conmemorativo',
            descriptionEn: 'Official team match jersey',
            backgroundColor: '#f59e0b',
            textColor: '#0f172a',
            isWinner: true,
            probabilityWeight: 10
        },
        {
            id: '1d4e5f6a-7b8c-4d9e-0f1a-2b3c4d5e6f70',
            textEs: '20% DESCUENTO',
            textEn: '20% OFF MERCH',
            descriptionEs: 'Cupón 20% descuento en tienda oficial',
            descriptionEn: '20% off at official merchandise booth',
            backgroundColor: '#2563eb',
            textColor: '#ffffff',
            isWinner: true,
            probabilityWeight: 15
        },
        {
            id: '2e5f6a7b-8c9d-4e0f-1a2b-3c4d5e6f7081',
            textEs: 'BOLETO DOBLE VIP',
            textEn: 'VIP PASS (2X)',
            descriptionEs: 'Entrada doble zona VIP próximo partido',
            descriptionEn: 'Double VIP ticket next match',
            backgroundColor: '#059669',
            textColor: '#ffffff',
            isWinner: true,
            probabilityWeight: 5
        },
        {
            id: '3f6a7b8c-9d0e-4f1a-2b3c-4d5e6f708192',
            textEs: 'PASE AMISTAD',
            textEn: 'FRIEND PASS',
            descriptionEs: 'Pase conmemorativo afición 2026',
            descriptionEn: 'Fan loyalty commemorative token',
            backgroundColor: '#7c3aed',
            textColor: '#ffffff',
            isWinner: false,
            probabilityWeight: 50
        }
    ];
};

// =========================================================================
// 6. SINTETIZADOR WEB AUDIO API (PersonalRouletteAudioEngine)
// =========================================================================

export class PersonalRouletteAudioEngine {
    constructor() {
        this.ctx = null;
        this.isMuted = this.loadMutedState();
    }

    loadMutedState() {
        try {
            return localStorage.getItem('personal_roulette_muted') === 'true';
        } catch {
            return false;
        }
    }

    initContext() {
        try {
            if (!this.ctx) {
                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                if (AudioCtx) {
                    this.ctx = new AudioCtx();
                }
            }
            if (this.ctx && this.ctx.state === 'suspended') {
                this.ctx.resume().catch(() => {});
            }
        } catch (e) {
            console.warn('AudioContext no soportado o bloqueado:', e);
        }
    }

    setMuted(muted) {
        this.isMuted = Boolean(muted);
        try {
            localStorage.setItem('personal_roulette_muted', String(this.isMuted));
        } catch {}
    }

    getMuted() {
        return this.isMuted;
    }

    toggleMuted() {
        this.setMuted(!this.isMuted);
        return this.isMuted;
    }

    /**
     * Genera un micropulso sonoro mecánico corto para simular el paso del puntero por un remache.
     * @param {number} speedMultiplier Multiplicador de velocidad (1 = normal, 0.4 = lento)
     */
    playMechanicalTick(speedMultiplier = 1) {
        if (this.isMuted) return;
        this.initContext();
        if (!this.ctx) return;

        try {
            const now = this.ctx.currentTime;
            const osc = this.ctx.createOscillator();
            const gain = this.ctx.createGain();
            const filter = this.ctx.createBiquadFilter();

            filter.type = 'bandpass';
            filter.frequency.setValueAtTime(1400, now);
            filter.Q.setValueAtTime(3.5, now);

            const startFreq = Math.min(1800, 950 * Math.max(0.6, speedMultiplier));
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(startFreq, now);
            osc.frequency.exponentialRampToValueAtTime(180, now + 0.028);

            gain.gain.setValueAtTime(0.35, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.028);

            osc.connect(filter);
            filter.connect(gain);
            gain.connect(this.ctx.destination);

            osc.start(now);
            osc.stop(now + 0.03);
        } catch (e) {
            // Ignorar errores de audio background
        }
    }

    /**
     * Genera un arpegio festivo y triunfal en síntesis aditiva.
     */
    playVictoryFanfare() {
        if (this.isMuted) return;
        this.initContext();
        if (!this.ctx) return;

        try {
            const now = this.ctx.currentTime;
            // Arpegio mayor triunfal: C5, E5, G5, C6, E6, G6
            const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51, 1567.98];
            const noteDurations = [0.12, 0.12, 0.12, 0.18, 0.22, 0.65];
            let offset = 0;

            notes.forEach((freq, idx) => {
                const noteTime = now + offset;
                const dur = noteDurations[idx];

                const osc = this.ctx.createOscillator();
                const subOsc = this.ctx.createOscillator();
                const gain = this.ctx.createGain();

                osc.type = idx === notes.length - 1 ? 'triangle' : 'sine';
                osc.frequency.setValueAtTime(freq, noteTime);

                subOsc.type = 'sine';
                subOsc.frequency.setValueAtTime(freq * 0.5, noteTime);

                gain.gain.setValueAtTime(0.001, noteTime);
                gain.gain.linearRampToValueAtTime(0.3, noteTime + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, noteTime + dur);

                osc.connect(gain);
                subOsc.connect(gain);
                gain.connect(this.ctx.destination);

                osc.start(noteTime);
                subOsc.start(noteTime);
                osc.stop(noteTime + dur + 0.05);

                offset += (idx < 3 ? 0.09 : 0.15);
            });
        } catch (e) {
            // Ignorar silenciosamente
        }
    }

    /**
     * Genera un doble beep agudo y limpio para confirmación de canje de premio al staff (HU-05).
     */
    playSuccessBeep() {
        if (this.isMuted) return;
        this.initContext();
        if (!this.ctx) return;

        try {
            const now = this.ctx.currentTime;
            const beeps = [
                { freq: 987.77, start: 0, dur: 0.07 },   // B5
                { freq: 1318.51, start: 0.09, dur: 0.12 } // E6
            ];

            beeps.forEach(({ freq, start, dur }) => {
                const noteTime = now + start;
                const osc = this.ctx.createOscillator();
                const gain = this.ctx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, noteTime);

                gain.gain.setValueAtTime(0.001, noteTime);
                gain.gain.linearRampToValueAtTime(0.28, noteTime + 0.012);
                gain.gain.exponentialRampToValueAtTime(0.001, noteTime + dur);

                osc.connect(gain);
                gain.connect(this.ctx.destination);

                osc.start(noteTime);
                osc.stop(noteTime + dur + 0.02);
            });
        } catch (e) {
            // Ignorar silenciosamente
        }
    }

    /**
     * Genera un tono bajo de advertencia / error para canje inválido o ya canjeado (HU-05).
     */
    playErrorBeep() {
        if (this.isMuted) return;
        this.initContext();
        if (!this.ctx) return;

        try {
            const now = this.ctx.currentTime;
            const beeps = [
                { freq: 240, start: 0, dur: 0.14 },
                { freq: 160, start: 0.16, dur: 0.22 }
            ];

            beeps.forEach(({ freq, start, dur }) => {
                const noteTime = now + start;
                const osc = this.ctx.createOscillator();
                const gain = this.ctx.createGain();
                const filter = this.ctx.createBiquadFilter();

                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(550, noteTime);

                osc.type = 'sawtooth';
                osc.frequency.setValueAtTime(freq, noteTime);

                gain.gain.setValueAtTime(0.001, noteTime);
                gain.gain.linearRampToValueAtTime(0.32, noteTime + 0.015);
                gain.gain.exponentialRampToValueAtTime(0.001, noteTime + dur);

                osc.connect(filter);
                filter.connect(gain);
                gain.connect(this.ctx.destination);

                osc.start(noteTime);
                osc.stop(noteTime + dur + 0.02);
            });
        } catch (e) {
            // Ignorar silenciosamente
        }
    }
}

export const rouletteAudioEngine = new PersonalRouletteAudioEngine();

// =========================================================================
// 7. RESPUESTA HÁPTICA (VIBRATION API)
// =========================================================================

/**
 * Dispara un micropulso háptico de 15ms para cada tic de la ruleta.
 */
export const triggerHapticTick = () => {
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(15);
        }
    } catch {}
};

/**
 * Dispara un patrón háptico festivo y victorioso.
 */
export const triggerHapticVictory = () => {
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate([100, 50, 100, 50, 200]);
        }
    } catch {}
};

// =========================================================================
// 8. WRAPPERS DE NEGOCIO RESTANTES (BL)
// =========================================================================

/**
 * Ejecuta el giro en el servidor (HU-02) con soporte de fallback resiliente.
 * @param {object} payload { sessionToken, roomId, ... }
 * @param {object} config Configuración de la sala
 * @param {string} lang Idioma 'es' | 'en'
 */
export const SpinRouletteBL = async (payload, config = {}, lang = 'es') => {
    if (!payload || !payload.sessionToken) {
        return { success: false, message: 'Token de sesión One-Time Token (OTT) no válido o expirado.' };
    }

    try {
        const response = await DLSpinRoulette(payload);

        if (!response.success || !response.data) {
            console.error('Error retornado por endpoint de giro:', response.message);
            return {
                success: false,
                message: response.message || 'No se pudo procesar el tiro en el servidor. Por favor intenta de nuevo.'
            };
        }

        const data = response.data;
        const prizes = (config.prizes && config.prizes.length >= 2) ? config.prizes : getDefaultPrizes(lang);

        let targetSliceIndex = typeof data.targetSliceIndex === 'number'
            ? data.targetSliceIndex
            : (typeof data.sliceIndex === 'number' ? data.sliceIndex : 0);

        if (data.prizeId || data.prize?.id) {
            const prizeId = data.prizeId || data.prize?.id;
            const foundIdx = prizes.findIndex(p => String(p.id).toLowerCase() === String(prizeId).toLowerCase());
            if (foundIdx >= 0) {
                targetSliceIndex = foundIdx;
            }
        }

        const prizeObj = prizes[targetSliceIndex] || prizes[0];
        const isWinner = data.isWinner ?? prizeObj.isWinner ?? false;
        const prizeText = lang === 'en'
            ? (data.prizeTextEn || data.prizeTitleEn || prizeObj.textEn || data.prizeTitle || 'Prize')
            : (data.prizeTextEs || data.prizeTitleEs || prizeObj.textEs || data.prizeTitle || 'Premio');

        return {
            success: true,
            data: {
                ...data,
                targetSliceIndex,
                isWinner,
                prizeText,
                prize: prizeObj,
                spinDurationMs: data.spinDurationMs || config.spinDurationMs || 5000,
                passCode: data.passCode || payload.passCode
            }
        };
    } catch (err) {
        console.error('Error en SpinRouletteBL:', err);
        return { success: false, message: 'Error de ejecución al girar la ruleta.' };
    }
};

// =========================================================================
// 8. GENERADOR DE CUPÓN DIGITAL CANVAS PNG (1080 x 800 px) & DESCARGA
// =========================================================================

/**
 * Carga una imagen de manera asíncrona permitiendo CORS.
 * @param {string} src 
 * @returns {Promise<HTMLImageElement|null>}
 */
const loadImageAsync = (src) => {
    return new Promise((resolve) => {
        if (!src) return resolve(null);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
};

/**
 * Traza un rectángulo con esquinas redondeadas en el contexto Canvas 2D.
 */
const drawRoundedRect = (ctx, x, y, w, h, r) => {
    let radius = r;
    if (w < 2 * radius) radius = w / 2;
    if (h < 2 * radius) radius = h / 2;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
};

/**
 * Genera el cupón digital oficial en Canvas PNG de alta definición (1080 x 800 px).
 * @param {object} params
 * @param {string} params.participantName Nombre completo del ganador
 * @param {string} params.passCode Folio único de canje
 * @param {string} params.prizeTitle Título del premio ganado
 * @param {string} [params.prizeIconUrl] URL del icono o fotografía del premio
 * @param {string} [params.logoUrl] Logotipo oficial del club/evento
 * @param {string} [params.primaryColor] Color primario temático (#dc2626)
 * @param {string} [params.qrDataUrl] Data URL del código QR para escaneo por staff
 * @param {string} [params.language] Idioma ('es' | 'en')
 * @param {string|Date} [params.createdAt] Fecha y hora de emisión
 * @returns {Promise<HTMLCanvasElement>} Elemento Canvas con el cupón renderizado
 */
export const generateVoucherCanvas = async ({
    participantName,
    passCode,
    prizeTitle,
    prizeIconUrl,
    logoUrl,
    primaryColor = '#dc2626',
    qrDataUrl,
    language = 'es',
    createdAt
}) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 800;
    const ctx = canvas.getContext('2d');
    const isEn = (language || 'es').toLowerCase().startsWith('en');

    // 1. Cargar recursos gráficos asíncronos en paralelo
    const [logoImg, prizeIconImg, qrImg] = await Promise.all([
        loadImageAsync(logoUrl),
        loadImageAsync(prizeIconUrl),
        loadImageAsync(qrDataUrl)
    ]);

    // 2. Fondo Deportivo Oscuro con Gradiente Radial y Lineal
    const bgGrad = ctx.createLinearGradient(0, 0, 0, 800);
    bgGrad.addColorStop(0, '#1d0a0a');
    // bgGrad.addColorStop(0, '#0a0f1d');
    bgGrad.addColorStop(0.5, '#c2231d');
    // bgGrad.addColorStop(0.5, '#1e293b');
    bgGrad.addColorStop(1, '#110606');
    // bgGrad.addColorStop(1, '#060911');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, 1080, 800);

    // Textura de líneas dinámicas deportivas diagonales
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.025)';
    ctx.lineWidth = 3;
    for (let x = -800; x < 2430; x += 45) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + 800, 800);
        ctx.stroke();
    }
    ctx.restore();

    // Resplandor temático centrado
    const centerGlow = ctx.createRadialGradient(540, 520, 60, 540, 520, 520);
    centerGlow.addColorStop(0, primaryColor ? `${primaryColor}28` : 'rgba(220, 38, 38, 0.18)');
    centerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = centerGlow;
    ctx.fillRect(0, 0, 1080, 800);

    // 3. Marco Exterior con Bisel Dorado Metálico (#eab308 / #f59e0b)
    const frameX = 36;
    const frameY = 36;
    const frameW = 1008;
    const frameH = 1278;
    const frameRadius = 32;

    const goldGrad = ctx.createLinearGradient(frameX, frameY, frameX + frameW, frameY + frameH);
    goldGrad.addColorStop(0, '#fef08a');
    goldGrad.addColorStop(0.25, '#f59e0b');
    goldGrad.addColorStop(0.5, '#fbbf24');
    goldGrad.addColorStop(0.75, '#d97706');
    goldGrad.addColorStop(1, '#78350f');

    ctx.save();
    drawRoundedRect(ctx, frameX, frameY, frameW, frameH, frameRadius);
    ctx.strokeStyle = goldGrad;
    ctx.lineWidth = 6;
    ctx.stroke();

    // Línea interior decorativa dorada
    drawRoundedRect(ctx, frameX + 16, frameY + 16, frameW - 32, frameH - 32, frameRadius - 10);
    ctx.strokeStyle = 'rgba(245, 158, 11, 0.45)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([8, 6]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Remaches dorados en las 4 esquinas
    const rivets = [
        { x: 64, y: 64 },
        { x: 1016, y: 64 },
        { x: 64, y: 1286 },
        { x: 1016, y: 1286 }
    ];
    rivets.forEach(rivet => {
        const rGrad = ctx.createRadialGradient(rivet.x - 2, rivet.y - 2, 1, rivet.x, rivet.y, 8);
        rGrad.addColorStop(0, '#ffffff');
        rGrad.addColorStop(0.4, '#fde047');
        rGrad.addColorStop(0.8, '#d97706');
        rGrad.addColorStop(1, '#78350f');
        ctx.fillStyle = rGrad;
        ctx.beginPath();
        ctx.arc(rivet.x, rivet.y, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#78350f';
        ctx.lineWidth = 1;
        ctx.stroke();
    });

    // 4. Encabezado Oficial y Logotipo
    let headerTextY = 200;
    if (logoImg) {
        const maxLogoW = 240;
        const maxLogoH = 75;
        const scale = Math.min(maxLogoW / logoImg.width, maxLogoH / logoImg.height);
        const drawW = logoImg.width * scale;
        const drawH = logoImg.height * scale;
        const drawX = 540 - (drawW / 2);
        const drawY = 82;
        ctx.drawImage(logoImg, drawX, drawY, drawW, drawH);
        headerTextY = drawY + drawH + 34;
    } else {
        // Corona / Estrella decorativa dorada
        ctx.save();
        ctx.font = '36px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('🏆', 540, 115);
        ctx.restore();
        headerTextY = 165;
    }

    // Texto: "CUPÓN OFICIAL DE PREMIO"
    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = '900 28px "Montserrat", sans-serif';
    ctx.fillStyle = '#fbbf24';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 8;
    ctx.fillText(isEn ? 'OFFICIAL PRIZE VOUCHER' : 'CUPÓN OFICIAL DE PREMIO', 540, headerTextY);

    ctx.font = '700 17px "Montserrat", sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.shadowBlur = 0;
    ctx.fillText(isEn ? 'SPIN AND WIN WITH XOLOS' : 'GIRA Y GANA CON XOLOS', 540, headerTextY + 28);
    ctx.restore();

    // 5. Recuadro Destacado para el Folio (passCode) con tipografía monoespaciada
    const folioBoxX = 90;
    const folioBoxY = headerTextY + 48;
    const folioBoxW = 900;
    const folioBoxH = 108;

    ctx.save();
    drawRoundedRect(ctx, folioBoxX, folioBoxY, folioBoxW, folioBoxH, 18);
    ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
    ctx.fill();
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 6]);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.textAlign = 'center';
    ctx.font = 'bold 15px "Montserrat", sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText(isEn ? 'DIGITAL REDEMPTION FOLIO' : 'FOLIO OFICIAL DE CANJE', 540, folioBoxY + 34);

    ctx.font = '900 46px "Courier New", monospace';
    ctx.fillStyle = '#fef08a';
    ctx.shadowColor = 'rgba(245, 158, 11, 0.4)';
    ctx.shadowBlur = 10;
    ctx.fillText(passCode || '#SD26-0000', 540, folioBoxY + 84);
    ctx.restore();

    // 6. Nombre del Ganador y Fecha/Hora
    const winnerY = folioBoxY + folioBoxH + 42;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = 'bold 30px "Montserrat", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText((participantName || (isEn ? 'VALUED FAN' : 'AFICIONADO')).toUpperCase(), 540, winnerY);

    ctx.font = '500 17px "Montserrat", sans-serif';
    ctx.fillStyle = '#94a3b8';
    const dateFormatted = formatEventDate(createdAt || new Date(), isEn ? 'en' : 'es');
    ctx.fillText(dateFormatted, 540, winnerY + 28);
    ctx.restore();

    // 7. Tarjeta Interna del Premio
    const prizeCardX = 90;
    const prizeCardY = winnerY + 45;
    const prizeCardW = 900;
    const prizeCardH = 145;

    ctx.save();
    drawRoundedRect(ctx, prizeCardX, prizeCardY, prizeCardW, prizeCardH, 20);
    const prizeCardGrad = ctx.createLinearGradient(prizeCardX, prizeCardY, prizeCardX + prizeCardW, prizeCardY + prizeCardH);
    prizeCardGrad.addColorStop(0, 'rgba(30, 41, 59, 0.95)');
    prizeCardGrad.addColorStop(1, 'rgba(15, 23, 42, 0.98)');
    ctx.fillStyle = prizeCardGrad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(245, 158, 11, 0.65)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.font = 'bold 16px "Montserrat", sans-serif';
    ctx.fillStyle = '#f59e0b';
    ctx.fillText(isEn ? '★ PRIZE AWARDED ★' : '★ PREMIO GANADO ★', 540, prizeCardY + 36);

    if (prizeIconImg) {
        const iconSize = 54;
        const iconX = 140;
        const iconY = prizeCardY + (prizeCardH / 2) - (iconSize / 2) + 10;
        ctx.drawImage(prizeIconImg, iconX, iconY, iconSize, iconSize);
    }

    ctx.font = 'bold 36px "Montserrat", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    ctx.shadowBlur = 8;
    ctx.fillText((prizeTitle || (isEn ? 'COMMEMORATIVE PRIZE' : 'PREMIO OFICIAL')).toUpperCase(), 540, prizeCardY + 95);
    ctx.restore();

    // 8. Código QR Nítido Renderizado en Contenedor Blanco
    //const qrBoxSize = 380;
    const qrBoxSize = 0;
    //const qrBoxX = 540 - (qrBoxSize / 2);
    const qrBoxY = prizeCardY + prizeCardH + 30;

    // ctx.save();
    // drawRoundedRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 24);
    // ctx.fillStyle = '#ffffff';
    // ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    // ctx.shadowBlur = 15;
    // ctx.fill();
    // ctx.strokeStyle = '#f59e0b';
    // ctx.lineWidth = 3;
    // ctx.stroke();

    // if (qrImg) {
    //     const qrPadding = 20;
    //     ctx.drawImage(
    //         qrImg,
    //         qrBoxX + qrPadding,
    //         qrBoxY + qrPadding,
    //         qrBoxSize - (qrPadding * 2),
    //         qrBoxSize - (qrPadding * 2)
    //     );
    // } else {
    //     // Fallback gráfico de QR
    //     ctx.fillStyle = '#0f172a';
    //     ctx.font = 'bold 22px "Courier New", monospace';
    //     ctx.textAlign = 'center';
    //     ctx.fillText(passCode || 'QR CODE', 540, qrBoxY + (qrBoxSize / 2));
    // }
    // ctx.restore();

    // Subtítulo del QR
    // ctx.save();
    // ctx.textAlign = 'center';
    // ctx.font = '600 18px "Montserrat", sans-serif';
    // ctx.fillStyle = '#cbd5e1';
    // ctx.fillText(
    //     isEn ? 'Present this QR code at the Official Booth to redeem' : 'Presenta este código QR en el Módulo Oficial para canjear',
    //     540,
    //     qrBoxY + qrBoxSize + 32
    // );
    // ctx.restore();

    // 9. Texto Legal de Validez y Deslinde al Pie
    const footerLineY = qrBoxY + qrBoxSize + 56;
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(140, footerLineY);
    ctx.lineTo(940, footerLineY);
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.font = '400 13.5px "Montserrat", sans-serif';
    ctx.fillStyle = '#64748b';
    if (isEn) {
        ctx.fillText('Valid exclusively during event date at the official on-site support booth.', 540, footerLineY + 24);
        ctx.fillText('Single-use voucher, non-transferable, no cash value. Not for resale.', 540, footerLineY + 44);
    } else {
        ctx.fillText('Válido exclusivamente durante la jornada del evento en el Módulo de Atención Oficial.', 540, footerLineY + 24);
        ctx.fillText('Cupón personal e intransferible, no canjeable por dinero. Prohibida su venta.', 540, footerLineY + 44);
    }

    ctx.font = 'bold 13px "Montserrat", sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('Powered by Interaktia & Xolos', 540, footerLineY + 70);
    ctx.restore();

    return canvas;
};

/**
 * Convierte un elemento Canvas a Blob PNG y dispara la descarga directa al almacenamiento del dispositivo.
 * @param {HTMLCanvasElement} canvas 
 * @param {string} filename 
 * @returns {Promise<boolean>}
 */
export const downloadCanvasAsPng = (canvas, filename = 'Cupon_Ruleta.png') => {
    return new Promise((resolve) => {
        try {
            if (!canvas) return resolve(false);

            const triggerLinkDownload = (url) => {
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            };

            if (canvas.toBlob) {
                canvas.toBlob((blob) => {
                    if (!blob) {
                        const dataUrl = canvas.toDataURL('image/png');
                        triggerLinkDownload(dataUrl);
                        resolve(true);
                        return;
                    }
                    const url = URL.createObjectURL(blob);
                    triggerLinkDownload(url);
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                    resolve(true);
                }, 'image/png', 1.0);
            } else {
                const dataUrl = canvas.toDataURL('image/png');
                triggerLinkDownload(dataUrl);
                resolve(true);
            }
        } catch (e) {
            console.error('Error al descargar Canvas como PNG:', e);
            resolve(false);
        }
    });
};

// =========================================================================
// 9. LISTENERS DE CAPTURA DE PANTALLA & TELEMETRÍA DE ACCIÓN (HU-03)
// =========================================================================

/**
 * Escucha atajos de teclado de captura de pantalla (PrintScreen) y heurística de cambio de visibilidad.
 * @param {string} participantId 
 * @param {string} passCode 
 * @param {Function} [onScreenshot] Callback al detectar evento
 * @param {string|null} [roomId] Identificador de sala
 * @returns {Function} Función de limpieza (cleanup)
 */
export const attachScreenshotListeners = (participantId, passCode, onScreenshot = null, roomId = null) => {
    if (typeof window === 'undefined') return () => {};

    let hasTrackedKey = false;
    let hasTrackedVis = false;

    const handleKeyUp = (e) => {
        if (e.key === 'PrintScreen' || e.keyCode === 44) {
            if (!hasTrackedKey) {
                hasTrackedKey = true;
                TrackActionBL(roomId, participantId, 'SCREENSHOT', 'KEYBOARD_SHORTCUT', passCode);
                if (typeof onScreenshot === 'function') {
                    onScreenshot('KEYBOARD_SHORTCUT');
                }
            }
        }
    };

    const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            if (!hasTrackedVis) {
                hasTrackedVis = true;
                TrackActionBL(roomId, participantId, 'SCREENSHOT', 'HEURISTIC', passCode);
                if (typeof onScreenshot === 'function') {
                    onScreenshot('HEURISTIC');
                }
            }
        }
    };

    window.addEventListener('keyup', handleKeyUp);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
        window.removeEventListener('keyup', handleKeyUp);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
};

/**
 * Registra una acción sobre el cupón digital (ej. DOWNLOAD, SCREENSHOT, RECOVER)
 * y la reporta tanto al backend como a Google Tag Manager / GA4 (HU-03).
 * @param {string|object} roomIdOrPayload 
 * @param {string} [participantId] 
 * @param {string} [action] 
 * @param {string} [method] 
 * @param {string|null} [passCode] 
 */
export const TrackActionBL = async (roomIdOrPayload, participantId, action, method, passCode = null) => {
    let payload = {};
    if (roomIdOrPayload && typeof roomIdOrPayload === 'object') {
        payload = {
            ...roomIdOrPayload,
            timestamp: roomIdOrPayload.timestamp || new Date().toISOString()
        };
    } else {
        payload = {
            roomId: roomIdOrPayload || null,
            participantId: participantId || null,
            action: action || 'ACTION',
            method: method || 'DIRECT',
            passCode: passCode || null,
            timestamp: new Date().toISOString()
        };
    }

    // Reporte a Google Tag Manager & Google Analytics 4
    try {
        const actionEvent = (payload.action || 'action').toLowerCase();
        if (window.dataLayer && Array.isArray(window.dataLayer)) {
            window.dataLayer.push({
                event: `personal_roulette_${actionEvent}`,
                action: payload.action,
                method: payload.method,
                participant_id: payload.participantId,
                pass_code: payload.passCode,
                room_id: payload.roomId
            });
        }
        if (window.gtag) {
            window.gtag('event', `personal_roulette_${actionEvent}`, {
                action: payload.action,
                method: payload.method,
                participant_id: payload.participantId,
                pass_code: payload.passCode,
                room_id: payload.roomId
            });
        }
    } catch (e) {
        console.warn('Error en telemetría de acción GTM/GA4:', e);
    }

    return await DLTrackAction(payload);
};

/**
 * Redimensiona y comprime una imagen en el cliente usando Canvas HTML5 (HU-04).
 * @param {File|Blob|string} file Archivo de imagen o dataURL
 * @param {number} maxWidth Ancho máximo en px (default: 1200)
 * @param {number} maxHeight Alto máximo en px (default: 1200)
 * @param {number} quality Calidad de compresión JPEG (0.1 a 1.0, default: 0.85)
 * @returns {Promise<string>} Base64 dataURL de la imagen optimizada
 */
export const compressImageFile = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.85) => {
    return new Promise((resolve, reject) => {
        if (!file) return resolve('');

        if (typeof file === 'string' && (file.startsWith('http://') || file.startsWith('https://'))) {
            return resolve(file);
        }

        const processImage = (imgSrc) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    let { width, height } = img;
                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.round(width * ratio);
                        height = Math.round(height * ratio);
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    const isPng = (file && file.type === 'image/png') || (typeof imgSrc === 'string' && imgSrc.startsWith('data:image/png'));
                    const outputType = isPng ? 'image/png' : 'image/jpeg';
                    const dataUrl = canvas.toDataURL(outputType, quality);
                    resolve(dataUrl);
                } catch (err) {
                    console.warn('Error al procesar Canvas para comprimir imagen:', err);
                    resolve(imgSrc);
                }
            };
            img.onerror = () => {
                resolve(typeof file === 'string' ? file : '');
            };
            img.src = imgSrc;
        };

        if (typeof file === 'string') {
            processImage(file);
        } else if (file instanceof Blob || file instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => processImage(e.target.result);
            reader.onerror = (e) => reject(e);
            reader.readAsDataURL(file);
        } else {
            resolve('');
        }
    });
};

/**
 * Helper asíncrono que precarga imágenes en la memoria caché del navegador creando instancias de Image() (HU-04).
 * @param {Array<string>} urls Arreglo de URLs o DataURLs de imágenes a precargar
 * @returns {Promise<Array<HTMLImageElement|null>>}
 */
export const preloadAssets = async (urls = []) => {
    if (!urls || !Array.isArray(urls)) return [];
    const validUrls = urls.filter(u => u && typeof u === 'string' && u.trim().length > 0);

    const promises = validUrls.map(url => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = url;
        });
    });

    try {
        return await Promise.all(promises);
    } catch (e) {
        console.warn('Error durante la precarga de assets de ruleta:', e);
        return [];
    }
};

/**
 * Exportador a Excel (.xlsx) con SheetJS (xlsx) conteniendo 3 hojas estructuradas (HU-04):
 * - Hoja 1: Participantes Totales (Folio, Nombre, Email, Teléfono, ZIP, Idioma, Ganador, Premio, Descargas, Intentos, Estado, Fecha Registro, Fecha Giro)
 * - Hoja 2: Ganadores y Canjes (Folio, Nombre, Premio, Canjeado SÍ/NO, Fecha Canje, Operador, Canal)
 * - Hoja 3: Resumen de Embudo y Stock (Totales de hitos del embudo, tasas de conversión y balance de stock físico)
 * @param {object} dataset Objeto con { participants: [], stats: {}, prizes: [], config: {} }
 * @param {string} roomName Nombre de la sala
 */
export const exportPersonalRouletteExcel = (dataset = {}, roomName = 'BajaCup') => {
    try {
        const participants = dataset.participants || dataset.items || (Array.isArray(dataset) ? dataset : []);
        const stats = dataset.stats || dataset.funnelStats || {};
        const prizes = dataset.prizes || dataset.config?.prizes || [];

        // --- HOJA 1: PARTICIPANTES TOTALES ---
        const sheet1Data = participants.map((p, idx) => {
            const isWinner = p.isWinner ?? (p.prize?.isWinner ?? false);
            return {
                '#': idx + 1,
                'Folio': p.passCode || p.participationTicket || p.folio || '-',
                'Nombre Completo': p.fullName || p.name || '-',
                'Correo Electrónico': p.email || '-',
                'Teléfono': p.phone || '-',
                'ZIP Code': p.zipCode || '-',
                'Idioma': (p.languageCode || p.language || 'es').toUpperCase(),
                '¿Ganador?': isWinner ? 'SÍ' : 'NO',
                'Premio Asignado': p.prizeTitleSnapshot || p.prizeText || p.prize?.textEs || (isWinner ? 'Premio Oficial' : 'Sigue Participando'),
                'Descargas Cupón': p.downloadCount || (p.voucherDownloaded ? 1 : 0),
                'Intentos': p.attemptNumber || 1,
                'Estatus': p.isArchived ? 'Archivado' : (p.status || (isWinner ? 'Ganador' : 'Participó')),
                'Fecha de Registro': formatEventDate(p.createdAtUtc || p.createdAt || p.registeredAt),
                'Fecha de Giro': (p.playedAtUtc || p.playedAt || p.spunAt || p.createdAtUtc || p.createdAt) ? formatEventDate(p.playedAtUtc || p.playedAt || p.spunAt || p.createdAtUtc || p.createdAt) : '-'
            };
        });

        // --- HOJA 2: GANADORES Y CANJES ---
        const winners = participants.filter(p => (p.isWinner ?? p.prize?.isWinner) || p.hasRedeemedPrize || p.isRedeemed || p.status === 'REDEEMED');
        const sheet2Data = winners.length > 0 ? winners.map((p, idx) => {
            const hasRedeemed = p.hasRedeemedPrize || p.isRedeemed || p.status === 'REDEEMED';
            return {
                '#': idx + 1,
                'Folio': p.passCode || p.participationTicket || p.folio || '-',
                'Nombre del Ganador': p.fullName || p.name || '-',
                'Premio': p.prizeTitleSnapshot || p.prizeText || p.prize?.textEs || 'Premio Oficial',
                '¿Canjeado?': hasRedeemed ? 'SÍ' : 'NO',
                'Fecha de Canje': hasRedeemed && (p.redeemedAtUtc || p.redeemedAt) ? formatEventDate(p.redeemedAtUtc || p.redeemedAt) : '-',
                'Operador / Módulo': p.redeemedBy || (hasRedeemed ? 'Módulo Principal' : '-'),
                'Canal de Registro': p.trafficSource || p.source || 'Directo'
            };
        }) : [{ 'Mensaje': 'No hay ganadores registrados aún en esta dinámica.' }];

        // --- HOJA 3: RESUMEN DE EMBUDO Y STOCK ---
        const conversion = stats.conversionRates || {};
        const sheet3Data = [
            { 'Sección / Métrica': '=== HITOS DEL EMBUDO DE CONVERSIÓN ===', 'Total': '', 'Detalle': '' },
            { 'Sección / Métrica': '1. Escaneos de Código QR (qr_scan)', 'Total': stats.qrScanCount ?? stats.qrScan ?? stats.qrScans ?? 0, 'Detalle': 'Lecturas de QR en material publicitario y pantallas' },
            { 'Sección / Métrica': '2. Ingresos a la Página (page_view)', 'Total': stats.pageViewCount ?? stats.pageView ?? stats.pageViews ?? 0, 'Detalle': 'Visitas únicas/sesiones a la pantalla de ruleta' },
            { 'Sección / Métrica': '3. Formularios Iniciados (form_start)', 'Total': stats.formStartCount ?? stats.formStart ?? stats.formStarts ?? 0, 'Detalle': 'Aficionados que comenzaron a llenar datos' },
            { 'Sección / Métrica': '4. Formularios Completados (form_complete)', 'Total': stats.formCompleteCount ?? stats.formComplete ?? stats.formCompletes ?? 0, 'Detalle': 'Registros exitosos con token OTT generado' },
            { 'Sección / Métrica': '5. Giros Iniciados (spin_start)', 'Total': stats.spinStartCount ?? stats.spinStart ?? stats.gameStart ?? 0, 'Detalle': 'Giro de la ruleta puesto en marcha' },
            { 'Sección / Métrica': '6. Giros Completados (spin_complete)', 'Total': stats.spinCompleteCount ?? stats.spinComplete ?? stats.gameComplete ?? 0, 'Detalle': 'Resultados entregados al participante' },
            { 'Sección / Métrica': '7. Cupones Guardados / Descargados', 'Total': stats.passDownloadCount ?? stats.voucherDownload ?? stats.downloadCount ?? 0, 'Detalle': 'Descargas y capturas de voucher digital' },
            { 'Sección / Métrica': '8. Premios Canjeados en Módulo', 'Total': stats.prizeRedeemedCount ?? stats.prizeRedeemed ?? stats.redeemedCount ?? 0, 'Detalle': 'Validaciones y entregas de premios físicos' },
            { 'Sección / Métrica': '', 'Total': '', 'Detalle': '' },
            { 'Sección / Métrica': '=== TASAS DE CONVERSIÓN ===', 'Total': '', 'Detalle': '' },
            { 'Sección / Métrica': 'Conversión: QR a Visita', 'Total': `${(conversion.scanToPage ?? 0).toFixed(1)}%`, 'Detalle': 'Efectividad del escaneo a carga' },
            { 'Sección / Métrica': 'Conversión: Visita a Registro', 'Total': `${(conversion.pageToFormStart ?? 0).toFixed(1)}%`, 'Detalle': 'Interés de aficionado en registrarse' },
            { 'Sección / Métrica': 'Conversión: Formulario a Giro', 'Total': `${(conversion.formToSpin ?? conversion.completeToGame ?? 0).toFixed(1)}%`, 'Detalle': 'Completación de giro tras registro' },
            { 'Sección / Métrica': 'Conversión: Ganador a Canje', 'Total': `${(conversion.winnerToRedeem ?? 0).toFixed(1)}%`, 'Detalle': 'Porcentaje de premios reclamados en sitio' },
            { 'Sección / Métrica': '', 'Total': '', 'Detalle': '' },
            { 'Sección / Métrica': '=== BALANCE DE STOCK FÍSICO ===', 'Total': '', 'Detalle': '' }
        ];

        if (prizes && prizes.length > 0) {
            prizes.forEach((pz, i) => {
                const stock = pz.totalStock ?? 0;
                const awarded = pz.awardedCount ?? 0;
                const remaining = Math.max(0, stock - awarded);
                sheet3Data.push({
                    'Sección / Métrica': `Sector #${i + 1}: ${pz.textEs || pz.textEn || 'Premio'} (${pz.isWinner ? 'Ganador' : 'Cortesía'})`,
                    'Total': `Total: ${stock} | Otorgados: ${awarded} | Restantes: ${remaining}`,
                    'Detalle': pz.isWinner ? `Stock disponible: ${remaining}` : 'Cortesía ilimitada'
                });
            });
        }

        // Crear libro XLSX
        const workbook = XLSX.utils.book_new();
        const ws1 = XLSX.utils.json_to_sheet(sheet1Data.length > 0 ? sheet1Data : [{ 'Aviso': 'Sin registros aún' }]);
        const ws2 = XLSX.utils.json_to_sheet(sheet2Data);
        const ws3 = XLSX.utils.json_to_sheet(sheet3Data);

        ws1['!cols'] = [
            { wch: 6 }, { wch: 16 }, { wch: 28 }, { wch: 28 }, { wch: 16 },
            { wch: 10 }, { wch: 8 }, { wch: 12 }, { wch: 26 }, { wch: 16 },
            { wch: 10 }, { wch: 14 }, { wch: 22 }, { wch: 22 }
        ];

        ws2['!cols'] = [
            { wch: 6 }, { wch: 16 }, { wch: 28 }, { wch: 28 }, { wch: 14 },
            { wch: 22 }, { wch: 22 }, { wch: 18 }
        ];

        ws3['!cols'] = [
            { wch: 45 }, { wch: 40 }, { wch: 55 }
        ];

        XLSX.utils.book_append_sheet(workbook, ws1, 'Participantes Totales');
        XLSX.utils.book_append_sheet(workbook, ws2, 'Ganadores y Canjes');
        XLSX.utils.book_append_sheet(workbook, ws3, 'Resumen de Embudo y Stock');

        const cleanRoom = (roomName || 'Sala').replace(/[^a-zA-Z0-9_-]/g, '_');
        const todayStr = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Reporte_Ruleta_BajaCup_${cleanRoom}_${todayStr}.xlsx`);

        return { success: true };
    } catch (error) {
        console.error('Error al exportar archivo Excel de Ruleta:', error);
        return { success: false, message: 'Error al generar el archivo Excel.' };
    }
};

/**
 * Obtiene la configuración administrativa de la ruleta (HU-04).
 * @param {string|null} roomId 
 */
export const GetAdminConfigBL = async (roomId = null) => {
    return await DLGetAdminConfig(roomId);
};

/**
 * Actualiza la configuración administrativa de la ruleta (HU-04).
 * @param {object} payload 
 * @param {string|null} roomId 
 */
export const UpdateAdminConfigBL = async (payload, roomId = null) => {
    const dataToSend = {
        ...payload,
        ...(roomId && !payload.roomId ? { roomId } : {})
    };
    return await DLUpdateAdminConfig(dataToSend);
};

/**
 * Obtiene las estadísticas administrativas del embudo de conversión (HU-04).
 * @param {string|null} roomId 
 */
export const GetAdminStatsBL = async (roomId = null) => {
    return await DLGetAdminStats(roomId);
};

/**
 * Obtiene el listado paginado y filtrado de participantes para la mesa de control (HU-04).
 * @param {string|null} roomId 
 * @param {number} page 
 * @param {number} pageSize 
 * @param {string} search 
 * @param {string} status 
 */
export const GetAdminParticipantsBL = async (roomId = null, page = 1, pageSize = 25, search = '', status = '') => {
    return await DLGetAdminParticipants(roomId, page, pageSize, search, status);
};

/**
 * Alterna el estado de activación de la ruleta (HU-04).
 * @param {boolean|object} payloadOrIsActive 
 * @param {string|null} roomId 
 */
export const ToggleActivePersonalRouletteBL = async (payloadOrIsActive, roomId = null) => {
    return await DLToggleActivePersonalRoulette(payloadOrIsActive, roomId);
};

/**
 * Reinicia por completo los datos de la sala en la ruleta (HU-04).
 * @param {object|string} payload 
 */
export const ResetDataPersonalRouletteBL = async (payload = {}) => {
    return await DLResetDataPersonalRoulette(payload);
};

/**
 * Obtiene el dataset completo para exportación a Excel (HU-04).
 * @param {string|null} roomId 
 */
export const GetExportDatasetBL = async (roomId = null) => {
    return await DLGetExportDataset(roomId);
};

/**
 * Extrae y normaliza un código de folio / pase a partir de un texto libre, URL o JSON escaneado por QR (HU-05).
 * @param {string} rawInput 
 * @returns {string}
 */
export const parseVoucherCode = (rawInput) => {
    if (!rawInput || typeof rawInput !== 'string') return '';
    const trimmed = rawInput.trim();

    // 1. Si es un JSON
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
            const parsed = JSON.parse(trimmed);
            const val = parsed.passCode || parsed.folio || parsed.voucher || parsed.participationTicket || parsed.code;
            if (val) return String(val).replace(/^#+/, '').trim().toUpperCase();
        } catch {}
    }

    // 2. Si es una URL con parámetros de búsqueda
    if (trimmed.includes('?') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        try {
            const url = new URL(trimmed.startsWith('http') ? trimmed : `https://dummy.local/${trimmed}`);
            const codeParam = url.searchParams.get('voucher') ||
                              url.searchParams.get('passCode') ||
                              url.searchParams.get('folio') ||
                              url.searchParams.get('code') ||
                              url.searchParams.get('p') ||
                              url.searchParams.get('ticket');
            if (codeParam) return codeParam.replace(/^#+/, '').trim().toUpperCase();
        } catch {}
    }

    // 3. Limpieza estándar: quitar hashtag si viene al inicio (ej. "#SD26-1234" -> "SD26-1234")
    return trimmed.replace(/^#+/, '').trim().toUpperCase();
};

/**
 * Consulta y pre-valida el folio, teléfono o QR de un participante para canje en sitio (HU-05).
 * @param {string|null} roomId 
 * @param {string} passCodeOrPhone 
 * @returns {Promise<{ success: boolean, status: string, message: string, data?: object }>}
 */
export const LookupVoucherBL = async (roomId = null, passCodeOrPhone = '') => {
    if (!passCodeOrPhone || !passCodeOrPhone.trim()) {
        return {
            success: false,
            status: 'EMPTY_INPUT',
            message: 'Ingresa o escanea un folio, teléfono o código QR válido.'
        };
    }

    const cleanInput = passCodeOrPhone.trim();
    const parsedCode = parseVoucherCode(cleanInput);
    const digitsOnly = cleanInput.replace(/\D/g, '');
    const isPhoneCandidate = digitsOnly.length === 10;

    const payload = {
        ...(roomId ? { roomId } : {}),
        passCode: parsedCode,
        search: cleanInput,
        ...(isPhoneCandidate ? { phone: digitsOnly } : {})
    };

    // 1. Intentar endpoint directo de lookup
    const lookupRes = await DLLookupVoucher(payload);
    if (lookupRes.success && lookupRes.data) {
        const p = lookupRes.data.participant || lookupRes.data;
        const isWinner = p.isWinner ?? (p.prize?.isWinner ?? false);
        const hasRedeemed = p.hasRedeemedPrize || p.isRedeemed || p.status === 'REDEEMED';

        let derivedStatus = lookupRes.status || 'WINNER_PENDING';
        if (hasRedeemed) {
            derivedStatus = 'ALREADY_REDEEMED';
        } else if (!isWinner) {
            derivedStatus = 'NOT_A_WINNER';
        } else {
            derivedStatus = 'WINNER_PENDING';
        }

        return {
            success: true,
            status: derivedStatus,
            message: lookupRes.message || 'Participante verificado.',
            data: {
                participant: p,
                ...lookupRes.data
            }
        };
    }

    // 2. Fallback de búsqueda a través de GetAdminParticipantsBL
    try {
        const searchRes = await DLGetAdminParticipants(roomId, 1, 10, parsedCode || cleanInput, 'all');
        if (searchRes.success && searchRes.data) {
            const items = searchRes.data.items || (Array.isArray(searchRes.data) ? searchRes.data : []);
            const match = items.find(item => {
                const itemCode = (item.passCode || item.participationTicket || item.folio || '').toUpperCase();
                const itemPhone = (item.phone || '').replace(/\D/g, '');
                const itemEmail = (item.email || '').toLowerCase();
                const cleanItemCode = itemCode.replace(/[^A-Z0-9]/g, '');
                const cleanSearch = (parsedCode || cleanInput).replace(/[^A-Z0-9]/g, '');

                return itemCode === parsedCode ||
                       (cleanSearch && cleanItemCode === cleanSearch) ||
                       (digitsOnly && itemPhone === digitsOnly) ||
                       (itemEmail && itemEmail === cleanInput.toLowerCase());
            }) || (items.length === 1 ? items[0] : null);

            if (match) {
                const isWinner = match.isWinner ?? (match.prize?.isWinner ?? false);
                const hasRedeemed = match.hasRedeemedPrize || match.isRedeemed || match.status === 'REDEEMED';

                let status = 'WINNER_PENDING';
                if (hasRedeemed) {
                    status = 'ALREADY_REDEEMED';
                } else if (!isWinner) {
                    status = 'NOT_A_WINNER';
                }

                return {
                    success: true,
                    status,
                    message: isWinner
                        ? (hasRedeemed ? 'Este premio ya ha sido entregado.' : 'Folio ganador verificado y listo para canjear.')
                        : 'El participante obtuvo un pase de cortesía sin premio físico.',
                    data: {
                        participant: match
                    }
                };
            }
        }
    } catch (fallbackErr) {
        console.warn('Fallback lookup error:', fallbackErr);
    }

    return {
        success: false,
        status: 'NOT_FOUND',
        message: 'No se encontró ningún registro con el folio o teléfono ingresado.'
    };
};

/**
 * Registra y ejecuta la entrega y canje físico de un premio en sitio (HU-05).
 * @param {object} params
 * @param {string|null} params.roomId
 * @param {string} params.passCode
 * @param {string} [params.redeemChannel] 'QR_SCAN' | 'MANUAL_FOLIO' | 'QUICK_AUTO'
 * @param {string} [params.operatorName]
 * @param {string} [params.notes]
 * @returns {Promise<{ success: boolean, status: string, message: string, data?: object }>}
 */
export const RedeemPrizeBL = async ({
    roomId = null,
    passCode = '',
    redeemChannel = 'MANUAL_FOLIO',
    operatorName = 'Staff',
    notes = ''
} = {}) => {
    if (!passCode || !passCode.trim()) {
        return {
            success: false,
            status: 'EMPTY_PASSCODE',
            message: 'El folio o código de pase es requerido para registrar el canje.'
        };
    }

    const cleanPassCode = parseVoucherCode(passCode);
    const payload = {
        ...(roomId ? { roomId } : {}),
        passCode: cleanPassCode,
        redeemChannel: redeemChannel || 'MANUAL_FOLIO',
        operatorName: operatorName || 'Staff',
        notes: notes || ''
    };

    const res = await DLRedeemPrize(payload);
    if (res.success) {
        return {
            success: true,
            status: 'REDEEMED_SUCCESS',
            message: res.message || '¡Premio canjeado y entregado con éxito!',
            data: res.data
        };
    }

    return {
        success: false,
        status: res.status || 'ERROR',
        message: res.message || 'No fue posible completar el canje del premio.'
    };
};

/**
 * Restituye la oportunidad de un participante archivando su intento previo (HU-06).
 * Permite que el aficionado vuelva a registrarse y girar con su mismo correo o teléfono.
 * @param {object} params
 * @param {string} params.participantId
 * @param {string} params.reason
 * @param {string} [params.operatorUser]
 * @param {string|null} [params.roomId]
 * @returns {Promise<{ success: boolean, message: string, data?: object }>}
 */
export const RestituteParticipantBL = async ({
    participantId = '',
    reason = '',
    operatorUser = 'Staff',
    roomId = null
} = {}) => {
    if (!participantId) {
        return {
            success: false,
            message: 'Identificador de participante requerido para la restitución.'
        };
    }

    const cleanReason = (reason || '').trim();
    if (!cleanReason || cleanReason.length < 3) {
        return {
            success: false,
            message: 'Debes proporcionar un motivo válido para la restitución (mínimo 3 caracteres).'
        };
    }

    const payload = {
        participantId,
        reason: cleanReason,
        operatorUser: operatorUser || 'Staff',
        ...(roomId ? { roomId } : {})
    };

    return await DLRestituteParticipant(payload);
};

/**
 * Actualiza el índice / puntero del paso actual en la fila de premios secuencial (HU-07).
 * Permite saltar inmediatamente a cualquier paso de la fila en 1 clic.
 * @param {string|null} roomId
 * @param {number} newIndex
 * @returns {Promise<{ success: boolean, message: string, data?: object }>}
 */
export const SetQueueIndexBL = async (roomId = null, newIndex = 0) => {
    const cleanIndex = Math.max(0, Number(newIndex) || 0);
    const payload = {
        ...(roomId ? { roomId } : {}),
        newIndex: cleanIndex,
        index: cleanIndex
    };
    return await DLSetQueueIndex(payload);
};

/**
 * Actualiza la lista ordenada de la fila de premios secuencial en caliente (HU-07).
 * Permite reordenar, agregar o remover pasos de la secuencia en tiempo real.
 * @param {string|null} roomId
 * @param {Array<string|object>} prizeQueueList
 * @returns {Promise<{ success: boolean, message: string, data?: object }>}
 */
export const UpdatePrizeQueueBL = async (roomId = null, prizeQueueList = []) => {
    if (!Array.isArray(prizeQueueList)) {
        return {
            success: false,
            message: 'La lista de la fila de premios debe ser un arreglo válido.'
        };
    }

    const payload = {
        ...(roomId ? { roomId } : {}),
        prizeQueue: prizeQueueList,
        prizeQueueList: prizeQueueList
    };
    return await DLUpdatePrizeQueue(payload);
};

/**
 * Activa o desactiva el Modo VIP (HU-07).
 * Garantiza victoria en el próximo tiro y permite opcionalmente forzar un premio específico.
 * @param {string|null} roomId
 * @param {boolean} isGuaranteed
 * @param {string|null} [overridePrizeId]
 * @returns {Promise<{ success: boolean, message: string, data?: object }>}
 */
export const ToggleVipBoostBL = async (roomId = null, isGuaranteed = false, overridePrizeId = null) => {
    const payload = {
        ...(roomId ? { roomId } : {}),
        isGuaranteed: Boolean(isGuaranteed),
        isGuaranteedWinner: Boolean(isGuaranteed),
        isNextSpinGuaranteedWinner: Boolean(isGuaranteed),
        overridePrizeId: overridePrizeId || null,
        guaranteedPrizeId: overridePrizeId || null
    };
    return await DLToggleVipBoost(payload);
};





